import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router-dom';

import { BrokerageList } from '@/components/BrokerageList/BrokerageList';
import { Callout } from '@/components/Callout/Callout';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { Spinner } from '@/components/Spinner/Spinner';
import { MY_BROKERAGES_QUERY, type MyBrokeragesResult } from '@/graphql/brokerages';
import { friendlyError } from '@/lib/keystone-errors';

import styles from './BrokeragesContainer.module.css';

/** `/brokerages`: the brokerages in your portfolio, each opening its own page. */
export function BrokeragesContainer() {
  const { data, loading, error } = useQuery<MyBrokeragesResult>(MY_BROKERAGES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const brokerages = data?.me.managedTenants ?? [];

  return (
    <div className={styles.page}>
      <PageHeader
        lede="The brokerages you look after. Open one to see its team, roles, branding and setup."
        title="Brokerages"
      />
      {error && !data ? (
        <Callout tone="danger" title="Brokerages couldn't be loaded">
          {friendlyError(error)}
        </Callout>
      ) : loading && !data ? (
        <p className={styles.loading} role="status">
          <Spinner /> Loading brokerages…
        </p>
      ) : brokerages.length === 0 ? (
        <EmptyState icon="buildings" title="You don't manage any brokerages">
          An admin can add brokerages for you from People & access.
        </EmptyState>
      ) : (
        <BrokerageList
          brokerages={brokerages}
          renderLink={(brokerage, children) => (
            <Link to={`/brokerages/${brokerage.id}`}>{children}</Link>
          )}
        />
      )}
    </div>
  );
}
